import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Dialog, Input, Popup, Toast } from 'antd-mobile';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { t } from '../../../i18n';
import dayjs from 'dayjs';
import truck2Svg from '../icons/svgs/truck2.svg';
import truckSvg from '../icons/svgs/truck.svg';
import truckPurpleSvg from '../icons/svgs/truck-purple.svg';
import {
  RouteType,
  WaypointStatus,
  PlanTimeType,
  WaypointType,
  TmsRouteBusinessType,
  TmsTaskClassificationType,
} from '../../../enums/TmsEnum';
import {
  TmsWarehouseOperationTask,
  GET_HAS_TMS_LOADING_DETAIL,
  HasTmsLoadingDetailResponse,
  GET_TMS_LOADING_DETAIL_SUMMARY,
  GetTmsLoadingDetailSummaryResponse,
  GET_NEEDS_TMS_UNLOADING_OPERATION,
  NeedsTmsUnloadingOperationResponse,
  GET_TMS_TASK_DRIVER_VEHICLE_INFO,
  GetTmsTaskDriverVehicleInfoResponse,
  UPDATE_TMS_TASK_HANDOVER_CARRIER_INFO,
} from '../../../graphql/queries/tmsTaskWarehouseOperationQueries';
import { GET_ACCOUNT_BIND_WAREHOUSE, GetAccountBindWarehouseResponse } from '../../../graphql/queries/warehouseQueries';
import { PdaPath } from '../../../enums/PdaNavigatePathEnum';
interface ButtonConfig {
  key: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  isPrimary?: boolean;
}

interface TmsTaskCardItemProps {
  task: TmsWarehouseOperationTask;
  showButtons?: boolean;
}

export const TmsTaskCardItem: React.FC<TmsTaskCardItemProps> = ({ task, showButtons = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [showCarrierPopup, setShowCarrierPopup] = useState(false);
  const [carrierForm, setCarrierForm] = useState({ driverName: '', phoneNumber: '', licensePlate: '', trailerNumber: '' });
  const [carrierFormLoading, setCarrierFormLoading] = useState(false);
  const routeVisualizationRef = useRef<HTMLDivElement>(null);

  // 获取当前登录用户的仓库ID
  const { data: warehouseData } = useQuery<GetAccountBindWarehouseResponse>(GET_ACCOUNT_BIND_WAREHOUSE, {
    fetchPolicy: 'cache-first'
  });
  const currentWarehouseId = warehouseData?.warehouse?.id;
  
  // 查询是否有装载明细数据
  const [checkHasTmsLoadingDetail] = useLazyQuery<HasTmsLoadingDetailResponse>(GET_HAS_TMS_LOADING_DETAIL);
  // 查询装载明细汇总（用于判断是否已全部提交容器信息）
  const [getLoadingDetailSummary] = useLazyQuery<GetTmsLoadingDetailSummaryResponse>(GET_TMS_LOADING_DETAIL_SUMMARY);
  // 实时查询交接任务司机/车辆信息（不使用缓存）
  const [getCarrierInfo] = useLazyQuery<GetTmsTaskDriverVehicleInfoResponse>(GET_TMS_TASK_DRIVER_VEHICLE_INFO, { fetchPolicy: 'no-cache' });
  const [updateCarrierInfo] = useMutation(UPDATE_TMS_TASK_HANDOVER_CARRIER_INFO);

  const {
    taskId,
    taskNumber = '',
    routeName = '',
    departureTime = '',
    isTikTok: isTikTokLegacy = false,
    businessType,
    routeType,
    waypointStatusId,
    waypointType,
    waypoints = [],
    licensePlate = '',
    trailerNumber = '',
    planTimeType,
    planTime = '',
    bolNumber = '',
    planTimeTimezone = '',
    hasUnsealTime = false,
    hasUploadBol = false,
    hasUnLoadingTime = false,
    carrierName,
    taskType,
  } = task;

  const isTikTok = businessType === TmsRouteBusinessType.TIKTOK;
  const isTikTokThirdParty = isTikTok && taskType === TmsTaskClassificationType.THIRD_PARTY_TASK;

  const isTemu = businessType === TmsRouteBusinessType.TEMU_TRANSFER || businessType === TmsRouteBusinessType.TEMU_SCF;

  // 根据路线类型获取样式 LINEHAUL-干线(黑色), FEEDER-支线(灰色), HANDOVER-交接(蓝色)
  const getRouteTypeStyle = (type: number) => {
    if (type === RouteType.FEEDER) {
      return 'bg-gray-400 text-white';
    }
    if (type === RouteType.HANDOVER) {
      return 'bg-blue-500 text-white';
    }
    return 'bg-black text-white';
  };

  // 根据途经点状态获取颜色
  const getWaypointStatusStyle = (statusId: number) => {
    switch (statusId) {
      case WaypointStatus.ARRIVED:
        return 'bg-green-400 text-black';
      case WaypointStatus.DEPARTED:
        return 'bg-blue-400 text-white';
      case WaypointStatus.PENDING_DEPARTURE:
        return 'bg-red-400 text-white';
      case WaypointStatus.PENDING_ARRIVAL:
      default:
        return 'bg-yellow-300 text-black';
    }
  };

  // 将后端返回的状态名称转换为国际化文本
  const getLocalizedWaypointStatus = (statusId: number) => {
    if (!statusId) return '';
    const statusMap: Record<number, string> = {
      [WaypointStatus.PENDING_ARRIVAL]: 'pda.pages.shipOut.waypointStatus.PENDING_ARRIVAL',
      [WaypointStatus.ARRIVED]: 'pda.pages.shipOut.waypointStatus.ARRIVED',
      [WaypointStatus.DEPARTED]: 'pda.pages.shipOut.waypointStatus.DEPARTED',
      [WaypointStatus.PENDING_DEPARTURE]: 'pda.pages.shipOut.waypointStatus.PENDING_DEPARTURE',
    };
    return t(statusMap[statusId] || '');
  };

  // 计算路线可视化中每个途经点的颜色
  // 返回一个对象，包含每个途经点索引对应的颜色状态
  const getRouteVisualizationColors = () => {
    if (!waypoints || waypoints.length === 0) {
      return {};
    }

    const colors: Record<number, { waypointColor: string; arrowColor: string }> = {};
    
    // 找到最后一个非"待到站"状态的途经点索引（已到站、待发车、已发车）
    let lastActiveIndex = -1;
    for (let i = waypoints.length - 1; i >= 0; i--) {
      if (waypoints[i].waypointStatus !== WaypointStatus.PENDING_ARRIVAL) {
        lastActiveIndex = i;
        break;
      }
    }
    
    // 找到最后一个已发车的途经点索引
    let lastDepartedIndex = -1;
    for (let i = waypoints.length - 1; i >= 0; i--) {
      if (waypoints[i].waypointStatus === WaypointStatus.DEPARTED) {
        lastDepartedIndex = i;
        break;
      }
    }
    
    // 遍历所有途经点，确定颜色
    waypoints.forEach((waypoint, index) => {
      // 途经点颜色：最后一个非"待到站"的点及之前的点为紫色
      const waypointColor = index <= lastActiveIndex ? 'text-purple-600' : 'text-gray-600';
      
      // 箭头颜色：最后一个已发车的点及之前的箭头为紫色
      const arrowColor = index <= lastDepartedIndex ? 'border-purple-600' : 'border-gray-400';
      
      colors[index] = { waypointColor, arrowColor };
    });
    
    return colors;
  };

  // 格式化时间显示
  const formatTime = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      return dayjs(dateStr).format('YYYY-MM-DD HH:mm');
    } catch {
      return dateStr;
    }
  };

  const getCurrentWaypointId = (): number | undefined => {
    const currentWaypoint = waypoints.find(wp => wp.waypointStatus === waypointStatusId);
    return currentWaypoint?.id;
  };

  const taskWaypointId = getCurrentWaypointId();
  const needUnloadingQuery =
    (waypointType === WaypointType.DESTINATION || waypointType === WaypointType.WAYPOINT) &&
    waypointStatusId === WaypointStatus.ARRIVED &&
    hasUnsealTime &&
    !hasUnLoadingTime;
  const { data: unloadingOperationData } = useQuery<NeedsTmsUnloadingOperationResponse>(
    GET_NEEDS_TMS_UNLOADING_OPERATION,
    {
      variables: { taskId: parseInt(taskId), taskWayPointId: taskWaypointId! },
      skip: !needUnloadingQuery || !taskWaypointId
    }
  );

  // 处理 waypointSortingCode 显示逻辑
  const getDisplayWaypointCode = (code: string | undefined): string => {
    if (!code) return '';
    // 如果包含 '-'，取最后一段；否则直接返回
    return code.includes('-') ? code.split('-').pop() || code : code;
  };

  /**
   * 点击「到达」按钮。
   * - 非交接任务：直接跳转到达页。
   * - 交接任务：实时查询司机/车牌/柜号，三者全部有值则直接跳转；
   *   任意一个为空则弹出补录弹窗，预填已有数据。
   *   查询异常时不阻塞操作，直接跳转。
   */
  const handleArrivalClick = async () => {
    if (routeType !== RouteType.HANDOVER) {
      navigate(PdaPath.PDA_TMS_ARRIVAL, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId: getCurrentWaypointId(), task, backRoute: location.pathname } });
      return;
    }
    setLoading(true);
    try {
      const { data } = await getCarrierInfo({ variables: { id: taskId } });
      const info = data?.getTmsTaskById;
      if (info?.driverName && info?.phoneNumber && info?.licensePlate && info?.trailerNumber) {
        navigate(PdaPath.PDA_TMS_ARRIVAL, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId: getCurrentWaypointId(), task, backRoute: location.pathname } });
      } else {
        setCarrierForm({
          driverName: info?.driverName ?? '',
          phoneNumber: info?.phoneNumber ?? '',
          licensePlate: info?.licensePlate ?? '',
          trailerNumber: info?.trailerNumber ?? '',
        });
        setShowCarrierPopup(true);
      }
    } catch {
      navigate(PdaPath.PDA_TMS_ARRIVAL, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId: getCurrentWaypointId(), task, backRoute: location.pathname } });
    } finally {
      setLoading(false);
    }
  };

  /**
   * 补录司机/车辆信息弹窗「确认」回调。
   * - 手机号填了则校验 10 位数字格式，不符合提示并终止。
   * - 只把有值的字段提交给后端更新；全部为空时跳过 mutation。
   * - 将已更新字段合并进 task 后跳转到达页，确保到达页展示最新数据。
   */
  const handleDriverVehicleInfoConfirm = async () => {
    if (!carrierForm.driverName.trim()) {
      Toast.show({ content: t('pda.pages.shipOut.arrivalCarrierDialog.driverNameRequired'), icon: 'fail' });
      return;
    }
    if (!carrierForm.phoneNumber.trim()) {
      Toast.show({ content: t('pda.pages.shipOut.arrivalCarrierDialog.phoneNumberRequired'), icon: 'fail' });
      return;
    }
    if (!/^\d{10}$/.test(carrierForm.phoneNumber.trim())) {
      Toast.show({ content: t('pda.pages.shipOut.arrivalCarrierDialog.phoneNumberInvalid'), icon: 'fail' });
      return;
    }
    if (!carrierForm.licensePlate.trim()) {
      Toast.show({ content: t('pda.pages.shipOut.arrivalCarrierDialog.licensePlateRequired'), icon: 'fail' });
      return;
    }
    if (!carrierForm.trailerNumber.trim()) {
      Toast.show({ content: t('pda.pages.shipOut.arrivalCarrierDialog.trailerNumberRequired'), icon: 'fail' });
      return;
    }
    setCarrierFormLoading(true);
    try {
      const input = {
        driverName: carrierForm.driverName.trim(),
        phoneNumber: carrierForm.phoneNumber.trim(),
        licensePlate: carrierForm.licensePlate.trim(),
        trailerNumber: carrierForm.trailerNumber.trim(),
      };
      await updateCarrierInfo({ variables: { id: taskId, input } });
      const updatedTask = { ...task, ...input };
      setShowCarrierPopup(false);
      navigate(PdaPath.PDA_TMS_ARRIVAL, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId: getCurrentWaypointId(), task: updatedTask, backRoute: location.pathname } });
    } catch {
      Toast.show({ content: t('common.error'), icon: 'fail' });
    } finally {
      setCarrierFormLoading(false);
    }
  };

  const handleLoadingClick = async () => {
    setLoading(true);
    try {
      const taskWaypointId = getCurrentWaypointId();
      const { data } = await checkHasTmsLoadingDetail({
        variables: {
          taskId: parseInt(taskId),
          taskWayPointId: taskWaypointId
        }
      });
      
      if (data?.hasTmsLoadingDetail) {
        // 如果已有装载明细数据，跳转到详情页面
        navigate(PdaPath.PDA_TMS_LOADING_DETAIL, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId, task, backRoute: location.pathname } });
      } else {
        // 否则跳转到扫描页面
        navigate(PdaPath.PDA_TMS_LOADING, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId, task, backRoute: location.pathname } });
      }
    } catch (error) {
      // 出错时默认跳转到扫描页面
      navigate(PdaPath.PDA_TMS_LOADING, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId: getCurrentWaypointId(), task, backRoute: location.pathname } });
    } finally {
      setLoading(false);
    }
  };

  const handleUnloadingClick = () => {
    const taskWaypointId = getCurrentWaypointId();
    if (task.unloadingScanSwitch) {
      navigate(PdaPath.PDA_TMS_UNLOADING, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId, task, backRoute: location.pathname } });
    } else {
      navigate(PdaPath.PDA_TMS_UNLOADING_DETAIL, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId, task, backRoute: location.pathname } });
    }
  };

  const handleSealingClick = async () => {
    const taskWaypointId = getCurrentWaypointId();
    setLoading(true);
    try {
      const { data: hasDetailData } = await checkHasTmsLoadingDetail({
        variables: { taskId: parseInt(taskId), taskWayPointId: taskWaypointId }
      });
      if (hasDetailData?.hasTmsLoadingDetail) {
        const { data: summaryData } = await getLoadingDetailSummary({
          variables: { taskId: parseInt(taskId), taskWayPointId: taskWaypointId }
        });
        const groups = summaryData?.getTmsLoadingDetailSummary?.destinationGroups ?? [];
        const hasIncomplete = groups.some(
          g => (Number(g.parcelQuantity) > 0 || Number(g.bagQuantity) > 0) && Number(g.containerQuantity ?? 0) === 0
        );
        if (hasIncomplete) {
          navigate(PdaPath.PDA_TMS_LOADING_DETAIL, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId, task, backRoute: location.pathname } });
          return;
        }
      }
      navigate(PdaPath.PDA_TMS_SEALING, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId, task, backRoute: location.pathname } });
    } catch (error) {
      console.error('Failed to check loading details before sealing:', error);
      navigate(PdaPath.PDA_TMS_SEALING, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId, task, backRoute: location.pathname } });
    } finally {
      setLoading(false);
    }
  };

  const handleUnsealingClick = () => {
    navigate(PdaPath.PDA_TMS_UNSEALING, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId: getCurrentWaypointId(), task, backRoute: location.pathname } });
  };

  const handleDepartureClick = () => {
    navigate(PdaPath.PDA_TMS_DEPARTURE, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId: getCurrentWaypointId(), task, backRoute: location.pathname } });
  };

  const handleUploadBolClick = () => {
    if (unloadingOperationData?.needsTmsUnloadingOperation) {
      navigate(PdaPath.PDA_TMS_UNLOADING_DETAIL, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId: getCurrentWaypointId(), task, backRoute: location.pathname } });
    } else {
      navigate(PdaPath.PDA_TMS_UPLOAD_BOL, { state: { taskId, routeName, waypointStatusId, waypointType, taskWaypointId: getCurrentWaypointId(), task, backRoute: location.pathname } });
    }
  };

  /**
   * 获取起点的可见按钮
   */
  const getOriginButtons = (currentStatus: number, currentUserWaypoint: any): ButtonConfig[] => {
    switch (currentStatus) {
      case WaypointStatus.PENDING_ARRIVAL: {
        const allPreviousDeparted = waypoints
          .filter(wp => wp.waypointOrder < currentUserWaypoint.waypointOrder)
          .every(wp => wp.waypointStatus === WaypointStatus.DEPARTED);
        return allPreviousDeparted ? [{ key: 'arrival', label: 'pda.pages.shipOut.arrival', onClick: handleArrivalClick }] : [];
      }
      case WaypointStatus.ARRIVED:
        return [
          { key: 'loading', label: 'pda.pages.shipOut.loading', onClick: handleLoadingClick, disabled: loading },
          { key: 'sealing', label: 'pda.pages.shipOut.sealing', onClick: handleSealingClick, disabled: loading }
        ];
      case WaypointStatus.PENDING_DEPARTURE:
        return [{ key: 'departure', label: 'pda.pages.shipOut.departure', onClick: handleDepartureClick, isPrimary: true }];
      default:
        return [];
    }
  };

  /**
   * 获取途经点的可见按钮
   */
  const getWaypointButtons = (currentStatus: number, currentUserWaypoint: any): ButtonConfig[] => {
    switch (currentStatus) {
      case WaypointStatus.PENDING_ARRIVAL: {
        const previousWaypoint = waypoints.find(wp => wp.waypointOrder === currentUserWaypoint.waypointOrder - 1);
        return previousWaypoint?.waypointStatus === WaypointStatus.DEPARTED ? [{ key: 'arrival', label: 'pda.pages.shipOut.arrival', onClick: handleArrivalClick }] : [];
      }
      case WaypointStatus.ARRIVED:
        if (!hasUnsealTime) {
          return [{ key: 'unsealing', label: 'pda.pages.shipOut.unsealing', onClick: handleUnsealingClick }];
        }
        const waypointButtons: ButtonConfig[] = [];
        if (!hasUnLoadingTime && unloadingOperationData?.needsTmsUnloadingOperation) {
          waypointButtons.push({ key: 'unloading', label: 'pda.pages.shipOut.unloading', onClick: handleUnloadingClick, disabled: loading });
        }
        waypointButtons.push(
          { key: 'loading', label: 'pda.pages.shipOut.loading', onClick: handleLoadingClick, disabled: loading },
          { key: 'sealing', label: 'pda.pages.shipOut.sealing', onClick: handleSealingClick, disabled: loading }
        );
        return waypointButtons;
      case WaypointStatus.PENDING_DEPARTURE:
        return [{ key: 'departure', label: 'pda.pages.shipOut.departure', onClick: handleDepartureClick, isPrimary: true }];
      default:
        return [];
    }
  };

  /**
   * 获取终点的可见按钮
   */
  const getDestinationButtons = (currentStatus: number, currentUserWaypoint: any): ButtonConfig[] => {
    switch (currentStatus) {
      case WaypointStatus.PENDING_ARRIVAL: {
        const previousWaypoint = waypoints.find(wp => wp.waypointOrder === currentUserWaypoint.waypointOrder - 1);
        return previousWaypoint?.waypointStatus === WaypointStatus.DEPARTED ? [{ key: 'arrival', label: 'pda.pages.shipOut.arrival', onClick: handleArrivalClick }] : [];
      }
      case WaypointStatus.ARRIVED:
        if (!hasUnsealTime) {
          return [{ key: 'unsealing', label: 'pda.pages.shipOut.unsealing', onClick: handleUnsealingClick }];
        }
        const buttons: ButtonConfig[] = [];
        if (!hasUnLoadingTime && unloadingOperationData?.needsTmsUnloadingOperation) {
          buttons.push({ key: 'unloading', label: 'pda.pages.shipOut.unloading', onClick: handleUnloadingClick, disabled: loading });
        }
        const isLinehaulNewFlow = routeType === RouteType.LINEHAUL &&
          (hasUnLoadingTime || unloadingOperationData?.needsTmsUnloadingOperation === true);
        if (!hasUploadBol && !isLinehaulNewFlow) {
          buttons.push({ key: 'uploadBol', label: 'pda.pages.shipOut.uploadBol', onClick: handleUploadBolClick });
        }
        return buttons;
      default:
        return [];
    }
  };

  /**
   * 检查是否应该显示按钮
   */
  const shouldShowButtons = (matchingWaypoints: any[]): boolean => {
    if (waypointType === WaypointType.DESTINATION) {
      const originWaypoint = matchingWaypoints.find(wp => wp.waypointOrder === 0);
      return !originWaypoint || originWaypoint.waypointStatus === WaypointStatus.DEPARTED;
    }
    
    if (waypointType === WaypointType.ORIGIN) {
      if (routeType === RouteType.HANDOVER) return true;
      const destinationWaypoint = matchingWaypoints.find(wp => wp.waypointOrder === waypoints.length - 1);
      return !destinationWaypoint || (destinationWaypoint.waypointStatus !== WaypointStatus.ARRIVED && destinationWaypoint.waypointStatus !== WaypointStatus.DEPARTED);
    }
    
    return true;
  };

  /**
   * 获取可见按钮
   * 根据当前用户所在途经点的类型和状态，返回应该显示的操作按钮
   * 处理同一用户在路线中有多个角色的情况（如LA既是起点又是终点）
   */
  const getVisibleButtons = (): ButtonConfig[] => {
    const matchingWaypoints = waypoints.filter(wp => wp.waypointId === currentWarehouseId?.toString());
    if (matchingWaypoints.length === 0) return [];
    
    // 根据 waypointType 找到正确的途经点
    let currentUserWaypoint;
    if (waypointType === WaypointType.ORIGIN) {
      currentUserWaypoint = matchingWaypoints.find(wp => wp.waypointOrder === 0);
    } else if (waypointType === WaypointType.DESTINATION) {
      currentUserWaypoint = matchingWaypoints.find(wp => wp.waypointOrder === waypoints.length - 1);
    } else {
      currentUserWaypoint = matchingWaypoints.find(wp => wp.waypointStatus === waypointStatusId);
    }
    
    if (!currentUserWaypoint || !shouldShowButtons(matchingWaypoints)) return [];
    
    const currentStatus = currentUserWaypoint.waypointStatus;
    
    // 根据途经点类型返回对应的按钮
    if (waypointType === WaypointType.ORIGIN) {
      return getOriginButtons(currentStatus, currentUserWaypoint);
    }
    
    if (waypointType === WaypointType.WAYPOINT) {
      return getWaypointButtons(currentStatus, currentUserWaypoint);
    }
    
    if (waypointType === WaypointType.DESTINATION) {
      return getDestinationButtons(currentStatus, currentUserWaypoint);
    }
    
    return [];
  };

  // 根据 waypointType 获取目标途经点(用于路线可视化途经点在中间显示)
  const getTargetWaypoint = useCallback(() => {
    if (!currentWarehouseId || !waypoints || waypoints.length === 0) return null;

    const currentWarehouseIdStr = currentWarehouseId.toString();
    const matchingWaypoints = waypoints.filter(wp => wp.waypointId === currentWarehouseIdStr);

    if (matchingWaypoints.length === 0) return null;

    if (matchingWaypoints.length === 1) {
      return matchingWaypoints[0];
    }

    // 如果有多个相同的仓库，需要根据 waypointType 判断是起点还是终点
    switch (waypointType) {
      case WaypointType.ORIGIN:
        return matchingWaypoints.find(wp => wp.waypointOrder === 0);
      case WaypointType.DESTINATION:
        return matchingWaypoints.find(wp => wp.waypointOrder === waypoints.length - 1);
      default:
        // 中转点，默认取第一个匹配的
        return matchingWaypoints[0];
    }
  }, [currentWarehouseId, waypoints, waypointType]);

  // 根据当前登录仓库，自动滚动到该仓库所在的途经点在中间
  useEffect(() => {
    const element = routeVisualizationRef.current;
    if (!element || element.scrollWidth <= element.clientWidth) {
      return;
    }

    // 使用 requestAnimationFrame 确保 DOM 完全渲染后再滚动
    const timer = requestAnimationFrame(() => {
      const targetWaypoint = getTargetWaypoint();

      if (!targetWaypoint) {
        // 如果找不到当前仓库，则默认滚动 50px
        element.scrollLeft = 50;
        return;
      }

      const currentWaypointIndex = waypoints.findIndex(wp => wp.id === targetWaypoint.id);
      if (currentWaypointIndex === -1) return;

      // 计算该途经点所在的水平位置
      const waypointElements = element.querySelectorAll('[data-waypoint-index]');
      const waypointElement = waypointElements[currentWaypointIndex] as HTMLElement;

      if (waypointElement) {
        const scrollLeft =
          waypointElement.offsetLeft +
          waypointElement.offsetWidth / 2 -
          element.clientWidth / 2;
        element.scrollLeft = Math.max(0, scrollLeft);
      }
    });

    return () => cancelAnimationFrame(timer);
  }, [waypoints, getTargetWaypoint]);

  return (
    <div className="relative bg-white rounded-lg p-2 shadow-sm">
      {carrierName && (
        <span className="absolute top-3 right-2 text-xs font-bold text-blue-500">
          {carrierName.length > 10 ? `${carrierName.slice(0, 10)}...` : carrierName}
        </span>
      )}
      {/* 路线信息头部 */}
      <div>
        <div className="flex items-center justify-between">
          <div className="text-base font-bold">
            {(() => {
              const isTikTokThirdParty = isTikTok && taskType === TmsTaskClassificationType.THIRD_PARTY_TASK;
              const isHandoverNonThirdParty = routeType === RouteType.HANDOVER && !isTikTokThirdParty;
              const displayRouteName = isHandoverNonThirdParty
                ? routeName.split('(')[0].trim()
                : routeName;
              return (
                <>
                  {displayRouteName}
                  {(!isTikTok || isHandoverNonThirdParty) && departureTime && <span className="text-base ml-2">({departureTime})</span>}
                  {isTikTokThirdParty && (
                    <div className="text-base mt-1">({taskNumber})</div>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* 标签区域 */}
        <div className="flex gap-2 mb-2">
          {(isTikTok || isTemu) && (
            <span className="bg-purple-500 text-white text-xs px-1 py-0.5 rounded">
              {isTikTok ? 'TIKTOK' : businessType === TmsRouteBusinessType.TEMU_SCF ? 'TEMU SCF' : 'TEMU GA'}
            </span>
          )}
          <span className={`${getRouteTypeStyle(routeType)} text-xs px-1 py-0.5 rounded`}>
            {routeType === RouteType.LINEHAUL ? t('pda.pages.shipOut.routeType.LINEHAUL') : routeType === RouteType.FEEDER ? t('pda.pages.shipOut.routeType.FEEDER') : t('pda.pages.shipOut.routeType.HANDOVER')}
          </span>
          <span className={`${getWaypointStatusStyle(waypointStatusId)} text-xs px-1 py-0.5 rounded`}>
            {getLocalizedWaypointStatus(waypointStatusId)}
          </span>
        </div>

        {/* 路线可视化：交接类型任务不显示 */}
        {routeType !== RouteType.HANDOVER
            && <div ref={routeVisualizationRef} className="flex items-center mt-2 mb-1 gap-0.5 overflow-x-auto py-2">
          {waypoints && waypoints.length > 0 ? ((): React.ReactNode => {
            const colors = getRouteVisualizationColors();
            // TikTok / Temu: 始发站用途经点展示，其余段用路线名称按 - 分隔（与 TikTok 一致）
            const splitRouteNameSegments = isTikTok || isTemu;
            const segments = splitRouteNameSegments
              ? (() => {
                  const routeParts = routeName.split('-').map(s => s.trim()).filter(Boolean);
                  const originDisplay = waypoints[0]
                    ? (getDisplayWaypointCode(waypoints[0].waypointSortingCode) || String(waypoints[0].waypointId))
                    : routeParts[0] ?? '';
                  return routeParts.length > 1 ? [originDisplay, ...routeParts.slice(1)] : [originDisplay];
                })()
              : waypoints.map(wp => getDisplayWaypointCode(wp.waypointSortingCode) || String(wp.waypointId));
            return segments.map((label, index) => {
              const isOrigin = index === 0;
              const waypointColor = splitRouteNameSegments
                ? (isOrigin ? (colors[0]?.waypointColor || 'text-gray-600') : 'text-gray-600')
                : (colors[index]?.waypointColor || 'text-gray-600');
              const arrowColor = splitRouteNameSegments
                ? (isOrigin ? (colors[0]?.arrowColor || 'border-gray-400') : 'border-gray-400')
                : (colors[index]?.arrowColor || 'border-gray-400');
              return (
                <React.Fragment key={index}>
                  <span
                    data-waypoint-index={index}
                    className={`text-[0.9rem] font-bold whitespace-nowrap flex-shrink-0 ${waypointColor}`}
                  >
                    {label}
                  </span>
                  {index < segments.length - 1 && (
                    <div className="flex items-center h-5 flex-1 gap-0 px-0.5 justify-center">
                      <div className={`flex items-center justify-center w-1 h-1 rounded-full ${arrowColor === 'border-purple-600' ? 'bg-purple-600' : 'bg-gray-600'}`}/>
                      <div className={`border-t-[1.5px] flex-1 max-w-[60px] ${arrowColor || 'border-gray-600'}`} />
                      <img
                        src={arrowColor === 'border-purple-600' ? truckPurpleSvg : truckSvg}
                        alt="truck"
                        className="w-7 h-7 flex-shrink-0"
                      />
                      <div className={`border-t-[1.5px] flex-1 max-w-[60px] ${arrowColor || 'border-gray-600'}`} />
                      <div className={`w-0 h-0 border-4 border-transparent flex-shrink-0 ${arrowColor === 'border-purple-600' ? 'border-l-purple-600' : 'border-l-gray-600'}`} />
                    </div>
                  )}
                </React.Fragment>
              );
            });
          })() : null}
        </div>}
      </div>

      {/* 车辆信息 */}
      <div className="mb-1 text-[0.8rem] text-gray-600 flex items-center gap-1.5">
        <img src={truck2Svg} alt="truck" className="w-5 h-5 flex-shrink-0" />
        {/*<span className="break-all">{licensePlate || '-'}</span>*/}
         <span className="break-all">{licensePlate || '-'}/{trailerNumber || '-'}</span>
      </div>

      {/* 计划时间和 BOL 信息 */}
      <div className="mb-1 text-[0.8rem]">
        {planTimeType === PlanTimeType.DEPARTURE && (isTikTokThirdParty || planTime) && (
          <div className="text-gray-600 break-all">
            <span>{t('pda.pages.shipOut.planDeparture')}:</span>
            <span className="ml-1">{isTikTokThirdParty ? '-' : formatTime(planTime!)}</span>
            {!isTikTokThirdParty && planTimeTimezone && <span className="text-gray-600 ml-1">{t(`enum.timezoneCode.${planTimeTimezone}`, planTimeTimezone)}</span>}
          </div>
        )}
        {planTimeType === PlanTimeType.ARRIVAL && planTime && (
          <div className="text-gray-600 break-all">
            <span>{t('pda.pages.shipOut.planArrival')}:</span>
            <span className="ml-1">{formatTime(planTime)}</span>
            {planTimeTimezone && <span className="text-gray-600 ml-1">{t(`enum.timezoneCode.${planTimeTimezone}`, planTimeTimezone)}</span>}
          </div>
        )}
        {bolNumber && (
          <div className="text-gray-600 break-all">
            <span>BOL:</span>
            <span className="ml-1">{bolNumber}</span>
          </div>
        )}
      </div>

      {/* 操作按钮 */}
      {showButtons && (
        <div className="flex justify-end gap-2">
          {getVisibleButtons().map((button) => (
            <Button
              key={button.key}
              size="small"
              fill="solid"
              onClick={button.onClick}
              disabled={button.disabled}
              style={{ backgroundColor: '#9333ea', color: 'white', border: 'none' }}
            >
              {t(button.label)}
            </Button>
          ))}
        </div>
      )}

      {/* 补充司机和车辆信息弹窗 */}
      <Popup
        visible={showCarrierPopup}
        onMaskClick={() => setShowCarrierPopup(false)}
        position="bottom"
        bodyStyle={{ borderRadius: '8px 8px 0 0', padding: '16px', maxHeight: '80vh', overflowY: 'auto' }}
      >
        <div className="text-base font-semibold text-center mb-4">
          {t('pda.pages.shipOut.arrivalCarrierDialog.title')}
        </div>
        <div className="mb-3">
          <div className="text-sm text-gray-600 mb-1">{t('pda.pages.shipOut.arrivalCarrierDialog.driverName')}</div>
          <div className="border border-gray-300 rounded px-2 py-1">
            <Input
              placeholder={t('pda.pages.shipOut.arrivalCarrierDialog.driverNamePlaceholder')}
              value={carrierForm.driverName}
              onChange={val => setCarrierForm(prev => ({ ...prev, driverName: val }))}
              maxLength={50}
            />
          </div>
        </div>
        <div className="mb-3">
          <div className="text-sm text-gray-600 mb-1">{t('pda.pages.shipOut.arrivalCarrierDialog.phoneNumber')}</div>
          <div className="border border-gray-300 rounded px-2 py-1">
            <Input
              placeholder={t('pda.pages.shipOut.arrivalCarrierDialog.phoneNumberPlaceholder')}
              value={carrierForm.phoneNumber}
              onChange={val => setCarrierForm(prev => ({ ...prev, phoneNumber: val }))}
              maxLength={10}
            />
          </div>
        </div>
        <div className="mb-3">
          <div className="text-sm text-gray-600 mb-1">{t('pda.pages.shipOut.arrivalCarrierDialog.licensePlate')}</div>
          <div className="border border-gray-300 rounded px-2 py-1">
            <Input
              placeholder={t('pda.pages.shipOut.arrivalCarrierDialog.licensePlatePlaceholder')}
              value={carrierForm.licensePlate}
              onChange={val => setCarrierForm(prev => ({ ...prev, licensePlate: val }))}
              maxLength={30}
            />
          </div>
        </div>
        <div className="mb-4">
          <div className="text-sm text-gray-600 mb-1">{t('pda.pages.shipOut.arrivalCarrierDialog.trailerNumber')}</div>
          <div className="border border-gray-300 rounded px-2 py-1">
            <Input
              placeholder={t('pda.pages.shipOut.arrivalCarrierDialog.trailerNumberPlaceholder')}
              value={carrierForm.trailerNumber}
              onChange={val => setCarrierForm(prev => ({ ...prev, trailerNumber: val }))}
              maxLength={50}
            />
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            block
            style={{ color: '#999999' }}
            onClick={() => setShowCarrierPopup(false)}
          >
            {t('pda.pages.shipOut.arrivalCarrierDialog.cancel')}
          </Button>
          <Button
            block
            color="primary"
            loading={carrierFormLoading}
            disabled={carrierFormLoading}
            onClick={handleDriverVehicleInfoConfirm}
          >
            {t('pda.pages.shipOut.arrivalCarrierDialog.confirm')}
          </Button>
        </div>
      </Popup>
    </div>
  );
};

export default TmsTaskCardItem;
